import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { SubmitHandler } from 'react-hook-form';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  useInviteCompanyMembership,
  getGetCompanyMembershipsByCompanyQueryOptions,
} from '@area-restrita/api-client/endpoints/company-memberships/company-memberships.gen';

import CompanyProvider from '@/contexts/CompanyContext';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import { ICompanyInviteForm } from '@/modules/companies/components/CompanyInviteForm/schema';
import CompanyUsers from '@/modules/companies/pages/CompanyUsers';
import { columns } from '@/modules/companies/pages/CompanyUsers/columns';
import { useQueryTable } from '@/hooks/useQueryTable';

import { Route as CompanyPageRoute } from './route';

export const Route = createFileRoute(
  '/_authenticated/_system/_tenant/companies/$companyId/users',
)({
  head: () => ({
    meta: [{ title: 'Usuários da Empresa' }],
  }),
  component: function CompanyUsersPage() {
    const { id } = CompanyPageRoute.useLoaderData();
    const { checkAuthorization } = useAuthSessionStore();

    const { query, table } = useQueryTable({
      columns,
      getQueryOptions: (params) =>
        getGetCompanyMembershipsByCompanyQueryOptions(id, params),
    });

    const queryClient = useQueryClient();
    const { mutateAsync } = useInviteCompanyMembership({
      mutation: {
        onSuccess: async () => {
          toast.success('Usuário convidado com sucesso!');
          await queryClient.invalidateQueries({
            queryKey: [`/api/v1/companies/${id}/memberships`],
          });
        },
        onError: () => {
          toast.error('Erro ao convidar usuário.');
        },
      },
    });

    const handleSubmit = useCallback<SubmitHandler<ICompanyInviteForm>>(
      (data) =>
        mutateAsync({
          companyId: id,
          data: {
            ...data,
            departments: data.departments.map(({ value }) => value),
          },
        }),
      [id, mutateAsync],
    );

    const canInvite = checkAuthorization(({ can }) =>
      can('create', 'company_memberships'),
    );

    return (
      <CompanyProvider companyId={id}>
        <CompanyUsers
          table={table}
          isLoading={query.isLoading}
          onSubmit={handleSubmit}
          canInvite={canInvite}
        />
      </CompanyProvider>
    );
  },
});
